import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { Logbook } from 'src/app/data-models/user-models/logbook.model';
import { Net } from 'src/app/data-models/user-models/net.model';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { UserData } from '../../data-models/user-models/user-data.model';
import { VesselData } from '../../data-models/user-models/vessel-data.model';
import { AddNetDialogComponent } from '../../dialogs/add-net-dialog/add-net-dialog.component';
import { ContactInfoDialogComponent } from '../../dialogs/contact-info-dialog/contact-info-dialog.component';
import { AuthenticationService } from '../../services/authentication.service';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { AddVesselDialogComponent } from '../../dialogs/add-vessel-dialog/add-vessel-dialog.component';
import { UpdateService } from 'src/app/services/update.service';
import packageJson from '../../../../package.json';


@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    standalone: false
})
export class ProfileComponent {

  // data preambles:
  isDataLoaded: boolean = false; // flag that tells us if the HTML is ready to be rendered
  userDetails: UserData = new UserData(); // get the current user details
  adminUser?: UserData = new UserData();
  logbooks: Logbook[] = [];
  vessels: VesselData[] = [];
  containsGillNet: boolean = false;
  nets: Net[] = [];
  groupedLogbooks: { [year: string]: Logbook[] } = {};
  years: string[] = [];
  isSyncing: boolean = false; // flag to disable the sync button while a push is in progress
  appVersion: string = packageJson.version;

  constructor(
    private dialog: MatDialog,
    private onlineStatusService: OnlineStatusService,
    public authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService,
    public updateService: UpdateService
  ) {
    this.userDetails = UserStorageService.currUser;
    this.adminUser = UserStorageService.adminUser;
    this.initialize();
  }


  /*  ---------- Page Initialization Methods  ---------- */


  // set page values
  initialize(): void {

    this.logbooks = this.userDetails.logbooks;
    this.vessels = this.userDetails.vessels;

    this.groupedLogbooks = {};
    for (let logbook of this.logbooks) {
      const year = logbook.seasonYear.toString();
      if (!this.groupedLogbooks[year]) {
        this.groupedLogbooks[year] = [];
      }
      this.groupedLogbooks[year].push(logbook);
    }

    this.years = Object.keys(this.groupedLogbooks).sort((a, b) => +b - +a);

    this.checkGillNet();

    this.isDataLoaded = true; // set flag to render HTML
    //this.ref.detectChanges(); // re-render HTML

  }


  // pushes all locally saved data up to Cloudant - gives users a manual recovery
  // path for trips that were saved while offline or while sync was failing
  syncToCloud(): void {

    if (this.onlineStatusService.getStatus() === OnlineStatusType.OFFLINE) {
      this.sharedMethodsService.toastOfflineSyncError();
      return;
    }

    this.isSyncing = true;
    UserStorageService.pushTripData().then((success: boolean) => {
      this.isSyncing = false;
      if (success) this.sharedMethodsService.toastTripDataSynced();
      else this.sharedMethodsService.toastSessionExpired();
    });

  }


  checkGillNet(): void {

    for (let logbook of this.logbooks) {
      if (logbook.gearName === 'Gill Net') {
        this.containsGillNet = true;
        this.nets = this.userDetails.nets;
        break;
      }
    }

  }


  /*  ----------Dialog Methods  ---------- */


  // lets the user enter a vessel
  addVesselDialog(): void {

    const dialogRef: MatDialogRef<AddVesselDialogComponent, any> = this.dialog.open(AddVesselDialogComponent, {
      width: '500px',
      height: '550px',
      data: { userDetails: this.userDetails }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result !== 'Back') {
        this.sharedMethodsService.toastVesselAdded();
      }
    });

  }


  // lets the user edit an existing vessel
  editVesselDialog(vessel: VesselData): void {

    const dialogRef: MatDialogRef<AddVesselDialogComponent, any> = this.dialog.open(AddVesselDialogComponent, {
      width: '500px',
      height: '550px',
      data: { userDetails: this.userDetails, vessel: vessel }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result === 'edit') {
        this.sharedMethodsService.toastVesselUpdated();
      }
    });

  }


  deleteVessel(vessel: VesselData, vesselIndex: number): void {

    // don't allow deletion if the vessel is used by the current trip or registered to a logbook
    if (UserStorageService.currTrip.vessel.vesselRegistrationNumber === vessel.vesselRegistrationNumber ||
        this.userDetails.logbooks.some(logbook => logbook.isRegistered && logbook.vessel.vesselRegistrationNumber === vessel.vesselRegistrationNumber)) {
      this.sharedMethodsService.toastVesselCannotDelete();
      return;
    }

    // Use the confirmation dialog to confirm deletion
    this.dialog.open(ConfirmationDialogComponent, {
      data: {screen: 'delete_vessel'}
    }).afterClosed().subscribe(result => {
      if (result) {
        this.userDetails.vessels.splice(vesselIndex, 1);
        this.vessels = this.userDetails.vessels;
        UserStorageService.saveUser(this.userDetails, true);
      }
    });

  }


  // edit contact info
  contactInfoDialog(): void {

    const dialogRef: MatDialogRef<ContactInfoDialogComponent, any> = this.dialog.open(ContactInfoDialogComponent, {
      width: '500px',
      height: '550px'
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.authenticationService.getUserDetails().then(() => {
          this.userDetails = UserStorageService.currUser;
          this.sharedMethodsService.toastProfileUpdated();
        });
      }
    });

  }

  // returns a net object from the result returned from the add net dialog 
  createNetFromDialog(result: any): Net {

    let newNet: Net = new Net(
      result.netType,
      result.netName.trim(),
      result.netLength,
      result.netDepth,
      result.netUnits,
      result.hangRatio,
      result.meshSize,
      result.meshSizeFraction,
      result.numMeshes,
      result.numStrands,
      true
    );


    return newNet;

  }

  // add new net to user profile
  addNetDialog(): void {

    const dialogRef: MatDialogRef<AddNetDialogComponent, any> = this.dialog.open(AddNetDialogComponent, {
      width: '500px',
      height: '550px',
      data: { userDetails: this.userDetails }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result !== 'delete') {
        let newNet: Net = this.createNetFromDialog(result);
        this.userDetails.nets.push(newNet);
        this.nets = this.userDetails.nets;
        UserStorageService.saveUser(this.userDetails, true).then(() => {
          this.sharedMethodsService.toastNetAdded();
        });
      }
    });

  }


  // lets the user edit an existing net
  editNetDialog(net: Net, netIndex: number): void {

    // If the net is on a report, don't allow editing (same rule as deletion)
    if (UserStorageService.currTrip.dailyCatchReports.some(dcr => dcr.netUsed.isEqual(net))) {
      this.sharedMethodsService.toastNetCannotEdit();
      return;
    }

    const dialogRef: MatDialogRef<AddNetDialogComponent, any> = this.dialog.open(AddNetDialogComponent, {
      width: '500px',
      height: '550px',
      data: { userDetails: this.userDetails, net: net }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result !== 'delete') {
        let updatedNet: Net = this.createNetFromDialog(result);
        updatedNet.isChecked = net.isChecked; // preserve the selection state of the original net
        this.userDetails.nets[netIndex] = updatedNet;
        this.nets = this.userDetails.nets;
        UserStorageService.saveUser(this.userDetails, true).then(() => {
          this.sharedMethodsService.toastNetUpdated();
        });
      }
    });

  }


  deleteNet(net: Net, netIndex: number) {
    //If the net is not on a report, don't allow deletion
    if (UserStorageService.currTrip.dailyCatchReports.some(dcr => dcr.netUsed.isEqual(net))) {
      this.sharedMethodsService.toastNetCannotDelete();
      return;
    }

    //Use the confirmation dialog to confirm deletion
    this.dialog.open(ConfirmationDialogComponent, {
      data: {screen: 'delete_net'}
    }).afterClosed().subscribe(result => {
      if (result) {
        UserStorageService.currUser.nets.splice(netIndex, 1);
        UserStorageService.saveUser(UserStorageService.currUser, true);
      }     
    });
  }

}
